import { useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

const authHeaders = () => ({ Authorization: getAccessTokenApi() });

/**
 * Wrapper del endpoint POST /incidents. Sin estado (sin useState); retorna
 * la promise del axios y deja al componente (modal) manejar loading / toast
 * / errores. Mismo patrón que useMenuItemsApi.
 */
const useIncidentApi = () => {
  /**
   * @param {Object}   params
   * @param {string}   params.category     - 'bug' | 'suggestion' | 'question' | 'other'
   * @param {string}   params.subject
   * @param {string}   params.description
   * @param {string}   [params.url]        - URL donde se originó el reporte
   * @param {Object}   [params.metadata]   - objeto libre (se serializa a JSON)
   * @param {string}   [params.tenantName]
   * @param {File[]}   [params.files]      - File objects desde el input/upload
   */
  const create = useCallback(async (params) => {
    const formData = new FormData();
    formData.append('category', params.category);
    formData.append('subject', params.subject);
    formData.append('description', params.description);
    if (params.url) formData.append('url', params.url);
    if (params.userAgent) formData.append('userAgent', params.userAgent);
    if (params.tenantName) formData.append('tenantName', params.tenantName);
    if (params.metadata && typeof params.metadata === 'object') {
      formData.append('metadata', JSON.stringify(params.metadata));
    }
    if (Array.isArray(params.files)) {
      for (const f of params.files) {
        formData.append('files', f);
      }
    }
    const { data } = await axios.post(`${API_PATH}/incidents`, formData, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }, []);

  const list = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) {
        if (v.length > 0) params.set(k, v.join(','));
      } else {
        params.set(k, String(v));
      }
    });
    const qs = params.toString();
    const { data } = await axios.get(`${API_PATH}/liftlogic/incidents${qs ? `?${qs}` : ''}`, {
      headers: authHeaders(),
    });
    return data;
  }, []);

  const detail = useCallback(async (id) => {
    const { data } = await axios.get(`${API_PATH}/liftlogic/incidents/${id}`, {
      headers: authHeaders(),
    });
    return data;
  }, []);

  const updateStatus = useCallback(async (id, status) => {
    const { data } = await axios.patch(
      `${API_PATH}/liftlogic/incidents/${id}/status`,
      { status },
      { headers: authHeaders() }
    );
    return data;
  }, []);

  const retryEmail = useCallback(async (id) => {
    const { data } = await axios.post(
      `${API_PATH}/liftlogic/incidents/${id}/retry-email`,
      {},
      { headers: authHeaders() }
    );
    return data;
  }, []);

  const reply = useCallback(async (id, { message, notify = true, newStatus = null } = {}) => {
    const { data } = await axios.post(
      `${API_PATH}/liftlogic/incidents/${id}/reply`,
      { message, notify, newStatus },
      { headers: authHeaders() }
    );
    return data;
  }, []);

  const retryReplyEmail = useCallback(async (id, replyId) => {
    const { data } = await axios.post(
      `${API_PATH}/liftlogic/incidents/${id}/replies/${replyId}/retry-email`,
      {},
      { headers: authHeaders() }
    );
    return data;
  }, []);

  return useMemo(
    () => ({ create, list, detail, updateStatus, retryEmail, reply, retryReplyEmail }),
    [create, list, detail, updateStatus, retryEmail, reply, retryReplyEmail]
  );
};

export default useIncidentApi;
